import React, { useContext, useEffect, useState } from 'react';
import { css, cx } from 'linaria';
import { theme } from '../Theme';
import { Price } from '../Price';
import { ReactComponent as AngleDown } from '../../svg/fa/angle-down.svg';
import { usePriceDiffer } from './usePriceDiffer';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItem,
  DropdownMenuItems
} from '@jetshop/ui/DropdownMenu';
import { useIntl } from '@jetshop/intl';
import { useLazyQuery, useQuery } from 'react-apollo';
import { WarehouseStockQuery } from './WarehouseStockQuery.gql';
import storeLocatorQuery from '../StoreLocator/StoreLocator.gql';
import ChannelContext from '@jetshop/core/components/ChannelContext';

const FAVORITE_STORE_KEY = 'favorite_store';

// Channel to region mapping
const countries = {
  'B2C-sv': 'Sverige',
  SV: 'Sverige',
  'B2C-dk': 'Danmark',
  DK: 'Danmark',
  'B2C-no': 'Norge',
  NO: 'Norge',
  'B2C-fi': 'Suomi',
  FI: 'Suomi',
  EU: 'Europa',
  NL: 'Nederland'
};

export function VariantSelector({
  product,
  variantHandler,
  voyadoFavoriteStore
}) {
  const [favoriteStore, setFavoriteStore] = useState(null);
  const { selectedChannel } = useContext(ChannelContext);

  // Get stores data
  const { data: storesData } = useQuery(storeLocatorQuery);

  const getAllStores = () => {
    const allStores = storesData?.stores || [];
    const currentRegion = countries[selectedChannel?.name];

    if (!currentRegion) {
      return allStores;
    }

    return allStores.filter(store => store.region === currentRegion);
  };

  // Initialize favorite store from localStorage or Voyado fallback (samma logik som StoreStock)
  useEffect(() => {
    if (!storesData?.stores) return;

    const availableStores = getAllStores();

    try {
      const storedFavoriteStore = localStorage.getItem(FAVORITE_STORE_KEY);
      if (storedFavoriteStore) {
        // Kontrollera att den lagrade favoriten fortfarande finns
        const storeExists = availableStores.find(
          store => store.name === storedFavoriteStore
        );
        if (storeExists) {
          setFavoriteStore(storedFavoriteStore);
          return;
        } else {
          localStorage.removeItem(FAVORITE_STORE_KEY);
        }
      }

      // Fallback till Voyado favorit
      if (voyadoFavoriteStore) {
        let voyadoStoreName = null;

        if (typeof voyadoFavoriteStore === 'string') {
          voyadoStoreName = voyadoFavoriteStore;
        } else if (voyadoFavoriteStore.name) {
          voyadoStoreName = voyadoFavoriteStore.name;
        } else if (voyadoFavoriteStore.favoriteStore?.name) {
          voyadoStoreName = voyadoFavoriteStore.favoriteStore.name;
        }

        if (voyadoStoreName) {
          // Hitta matchande butik
          let matchingStore = availableStores.find(
            store => store.name === voyadoStoreName
          );

          // Fuzzy match om ingen exakt match
          if (!matchingStore) {
            matchingStore = availableStores.find(
              store =>
                store.name
                  .toLowerCase()
                  .includes(voyadoStoreName.toLowerCase()) ||
                voyadoStoreName.toLowerCase().includes(store.name.toLowerCase())
            );
          }

          if (matchingStore) {
            setFavoriteStore(matchingStore.name);
          }
        }
      }
    } catch (e) {}
  }, [storesData?.stores, selectedChannel?.name, voyadoFavoriteStore]);

  // Listen to custom favorite store change events
  useEffect(() => {
    const handleFavoriteStoreChange = event => {
      setFavoriteStore(event.detail);
    };

    window.addEventListener('favoriteStoreChanged', handleFavoriteStoreChange);

    return () => {
      window.removeEventListener(
        'favoriteStoreChanged',
        handleFavoriteStoreChange
      );
    };
  }, []);

  // Revert to original single warehouse stock query
  const [getWarehouseStock, { data: warehouseData }] = useLazyQuery(
    WarehouseStockQuery,
    {
      variables: {
        articleNumber: product?.articleNumber
      }
    }
  );

  // Revert trigger warehouse stock query to original
  useEffect(() => {
    if (product?.articleNumber) {
      getWarehouseStock();
    }
  }, [product?.articleNumber, getWarehouseStock]);

  const hasPriceDiffer = usePriceDiffer({
    variants: product?.variants?.values
  });

  const variantOptions = product?.variants?.options;

  return (
    <div className={sharedStyles}>
      {variantOptions.map((option, index) => {
        return (
          <DropdownVariantSelect
            key={option.name}
            option={option}
            doNotDisable={index === 0}
            variantHandler={variantHandler}
            hasPriceDiffer={hasPriceDiffer}
            favoriteStore={favoriteStore}
            warehouseData={warehouseData}
          />
        );
      })}
    </div>
  );
}

function DropdownVariantSelect({
  option,
  variantHandler,
  doNotDisable,
  hasPriceDiffer,
  favoriteStore,
  warehouseData
}) {
  const {
    getSelectedValue,
    validateSelection,
    selectValue,
    selectedVariant,
    getVariantForSelection
  } = variantHandler;
  const t = useIntl();
  const selectedValue = getSelectedValue(option);

  // Revert getFavoriteStoreStock to original logic
  const getFavoriteStoreStock = variant => {
    if (!favoriteStore || !warehouseData?.product?.variants?.values) {
      return null;
    }

    const variantData = warehouseData.product.variants.values.find(
      v => v.articleNumber === variant.articleNumber
    );

    if (!variantData?.warehouseStock) {
      return null;
    }

    const favoriteStoreStock = variantData.warehouseStock.find(
      stock => stock.location && stock.location.name === favoriteStore
    );

    return favoriteStoreStock ? favoriteStoreStock.stockLevel > 0 : false;
  };

  return (
    <div className={dropdownStyles}>
      <label htmlFor={`option-${option.name}`}>{option.name}</label>
      <DropdownMenu>
        <DropdownMenuButton id={`option-${option.name}`}>
          {selectedValue ? (
            <>
              <span>{`${option.name}: ${selectedValue}`}</span>
              <div className="status-container">
                <span
                  className={cx(
                    'chosenStockStatus',
                    (
                      selectedVariant
                        ? selectedVariant?.stockStatus.buyable
                        : null
                    )
                      ? 'buyable'
                      : 'not-buyable'
                  )}
                >
                  {selectedVariant
                    ? selectedVariant?.stockStatus.text
                        .replace(/\([^)]*\)/, '')
                        .trim()
                    : null}
                </span>
                {favoriteStore && (
                  <span
                    className={cx(
                      'favoriteStoreStatus',
                      getFavoriteStoreStock(selectedVariant)
                        ? 'in-stock'
                        : 'not-in-stock'
                    )}
                  >
                    {favoriteStore}:{' '}
                    {getFavoriteStoreStock(selectedVariant)
                      ? t('In stock')
                      : t('Out of stock')}
                  </span>
                )}
              </div>
            </>
          ) : (
            `${t('Select')}: ${option.name}`
          )}
          <AngleDown />
        </DropdownMenuButton>
        <DropdownMenuItems style={{ zIndex: 9999 }}>
          {option.values.map(value => {
            const validation = validateSelection(value, option);
            const variant = getVariantForSelection(value, option);
            const favoriteStoreHasStock = getFavoriteStoreStock(variant);

            // Do not render products with missing prices
            if (variant?.price?.incVat === 0) {
              return <div style={{ display: 'none' }} />;
            }

            return (
              <DropdownMenuItem
                data-testid={value + option.name}
                key={value + option.name}
                disabled={doNotDisable && validation === 'invalid'}
                onSelect={({ setIsOpen }) => {
                  selectValue(value, option);
                  setIsOpen(false);
                }}
              >
                <span className="variant-value">{value}</span>
                <div className="stock-status-container">
                  <span
                    className={cx(
                      'stockStatus',
                      variant.stockStatus.buyable ? 'buyable' : 'not-buyable'
                    )}
                  >
                    {variant.stockStatus.text.replace(/\([^)]*\)/, '').trim()}
                  </span>
                  {favoriteStore && (
                    <span
                      className={cx(
                        'favoriteStoreStatus',
                        favoriteStoreHasStock ? 'in-stock' : 'not-in-stock'
                      )}
                    >
                      {favoriteStore}:{' '}
                      {favoriteStoreHasStock
                        ? t('In stock')
                        : t('Out of stock')}
                    </span>
                  )}
                </div>
                {hasPriceDiffer && (
                  <span className="price">
                    <Price
                      price={variant.price}
                      previousPrice={variant.previousPrice}
                    />
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuItems>
      </DropdownMenu>
    </div>
  );
}

export const dropdownStyles = css`
  // && wrapping needed to avoid non-deterministic styling
  && {
    > label {
      display: none;
    }
    margin: 1em 0;
    width: 100%;
    [data-flight-dropdown] {
      background: white;
    }
    [data-flight-dropdown-button] {
      justify-content: space-between;
      width: 100%;
      height: auto;
      min-height: 42px;
      display: flex;
      align-items: center;
      background-color: rgb(255, 255, 255);
      position: relative;
      cursor: pointer;
      border: 1px solid rgb(0, 0, 0);
      padding: 8px 10px;
      outline: 0;
      font-weight: bold;
      font-size: 14px;
      line-height: 1;
      border-radius: 0;

      // Make the main content area take available space
      > span:first-child {
        flex: 1;
        text-align: left;
      }

      .status-container {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 2px;
        margin-left: auto;
        margin-right: 10px;
        flex-shrink: 0;
      }

      svg {
        margin-left: 5px;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .chosenStockStatus {
        text-align: right;
        font-size: 12px;
        white-space: nowrap;
        &.buyable {
          color: ${theme.colors.accent};
        }
        &.not-buyable {
          color: ${theme.colors.orange};
        }
      }

      .favoriteStoreStatus {
        text-align: right;
        font-size: 12px;
        font-weight: normal;
        white-space: nowrap;
        &.in-stock {
          color: #235837;
        }
        &.not-in-stock {
          color: #c25100;
        }
      }
    }
  }
  [data-flight-dropdown-open='true'] {
    [data-flight-dropdown-button] {
      border-bottom-color: transparent;
    }
  }
  [data-flight-dropdown-items] {
    width: 100%;
    border: 1px solid #dedede;
    margin-top: -1px;
  }
  [data-flight-dropdown-item] {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    font-size: 14px;
    padding: 8px 20px;
    border-top: 0;
    white-space: pre-wrap !important;
    &:hover {
      background: #f7f7f7 !important;
      outline: 0;
    }
    .variant-value {
      margin-right: auto;
      align-self: center;
    }

    .stock-status-container {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;
      margin-left: 10px;
    }

    .stockStatus {
      text-align: right;
      font-size: 12px;
    }

    .favoriteStoreStatus {
      text-align: right;
      font-size: 12px;
      b &.in-stock {
        color: #235837;
      }
      &.not-in-stock {
        color: #c25100;
      }
    }

    .buyable {
      color: ${theme.colors.accent};
    }
    .not-buyable {
      color: ${theme.colors.orange};
    }
    .price {
      color: #000;
      text-align: right;
      font-size: 12px;
      margin-left: 10px;
      align-self: center;
      > div {
        justify-content: flex-start;
        display: flex;
        div {
          margin-left: 10px;
        }
        .new-price {
          color: ${theme.colors.red};
          white-space: nowrap;
          width: auto;
          font-weight: bold;
          margin-top: -1px;
        }
        .old-price {
          color: ${theme.colors.grey};
          white-space: nowrap;
          width: auto;
          font-weight: normal;
        }
      }
    }
  }

  &.invalid [data-flight-dropdown-button] {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }
`;

const sharedStyles = css`
  width: 100%;
  label {
    display: flex;
    padding-bottom: 0.25em;
    margin-top: 1em;
    font-size: 0.875em;
    align-items: center;
  }
  .invalid {
    label {
      color: #c25100;
    }
  }
  .missingVariant {
    margin-right: 0.25em;
    height: 10px;
    width: 10px;
  }
`;
